import { schemeTableau10 } from "d3"
import type { AgentName } from "@/lib/agentcore-client"
import type { TokenUsageResponse, TokenCounts } from "@/services/resourceUsageService"
import type { StackedBarDatum } from "../shared/StackedBarChart"

/**
 * The three token metrics that each get their own chart.
 */
export type TokenMetric = keyof TokenCounts

/**
 * Fixed colour per canonical agent.
 *
 * Keyed off `AgentName` so every known agent always renders in the same colour
 * across charts, sessions, and page loads — regardless of which agents appear
 * in a given response or their ordering. Because it is typed as
 * `Record<AgentName, string>`, adding a new agent to `AgentName` is a compile
 * error here until a colour is assigned, keeping the map exhaustive.
 *
 * Colours are drawn from d3's Tableau 10 palette (distinct, print-friendly).
 */
export const AGENT_COLORS: Record<AgentName, string> = {
  "deep-literature-review": schemeTableau10[0], // blue
  "exploratory-literature-review": schemeTableau10[1], // orange
  "survey-variable-discovery": schemeTableau10[2], // red
}

// Neutral grey for any agent ID not present in AGENT_COLORS
const UNKNOWN_AGENT_COLOR = "#9ca3af"

/**
 * Return the fixed colour for an agent ID.
 *
 * Known agents map to their assigned colour in AGENT_COLORS; any unrecognised
 * ID falls back to a neutral grey.
 *
 * @param agent - The canonical kebab-case agent ID.
 * @returns A hex colour string.
 */
export function colorForAgent(agent: string): string {
  return AGENT_COLORS[agent as AgentName] ?? UNKNOWN_AGENT_COLOR
}

/**
 * Transform the API response into StackedBarChart data for a single metric.
 *
 * Produces one datum per day (preserving the response's day order), each with a
 * per-agent value for the chosen metric.
 *
 * @param response - The token usage API response.
 * @param metric - Which counter to extract (inputTokens/outputTokens/totalTokens).
 * @returns An ordered array of stacked-bar data for the metric.
 */
export function toChartData(
  response: TokenUsageResponse,
  metric: TokenMetric
): StackedBarDatum[] {
  return response.usage.map((day) => {
    const values: Record<string, number> = {}
    for (const agent of response.agents) {
      // Backend zero-fills, but default to 0 defensively for type safety.
      values[agent] = day.agents[agent]?.[metric] ?? 0
    }
    return { label: day.date, values }
  })
}

/**
 * Format an ISO date (YYYY-MM-DD) as a short weekday+day+month label.
 *
 * @param isoDate - An ISO date string (YYYY-MM-DD).
 * @returns A short label like "Mon 1 Sep", or the raw string if unparseable.
 */
export function formatDayLabel(isoDate: string): string {
  const parts = isoDate.split("-").map((part) => Number(part))
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return isoDate
  }

  const [year, month, day] = parts
  // Construct in local time; month is 0-indexed in the Date constructor.
  const date = new Date(year, month - 1, day)

  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  })
}

/**
 * Turn a canonical kebab-case agent ID into a human-readable label.
 *
 * e.g. "exploratory-literature-review" -> "Exploratory Literature Review".
 *
 * @param agentId - The canonical kebab-case agent ID.
 * @returns A title-cased, space-separated label.
 */
export function formatAgentLabel(agentId: string): string {
  return agentId
    .split("-")
    .filter((word) => word.length > 0)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ")
}
